import type { IModalConfig } from './types'

/**
 * Класс для управления конфигурациями модальных окон.
 */
export class ModalConfig {
  private static configs: Map<string, IModalConfig> = new Map()

  /**
   * Конфигурация по умолчанию для модального окна.
   */
  private static readonly DEFAULT_CONFIG: Partial<IModalConfig> = {
    preventDefault: true,
    lockFocus: true,
    startFocus: true,
    focusBack: true,
    resetScrollPos: false,
    eventTimeout: 500,
    openOver: false,
    disableUserClose: false
  }

  /**
   * Добавляет конфигурацию для модального окна.
   * @param {string} name - Имя модального окна.
   * @param {IModalConfig} config - Конфигурация модального окна.
   * @param {boolean} [force=false] - Принудительно добавить модальное окно, даже если оно уже существует.
   */
  public static add(
    name: string,
    config: IModalConfig,
    force: boolean = false
  ): void {
    if (!force && this.configs.has(name)) {
      // eslint-disable-next-line no-console
      console.debug(`modal "${name}" is already added`, this.configs.get(name))
      return
    }

    this.configs.set(name, {
      ...this.DEFAULT_CONFIG,
      ...config
    })
  }

  /**
   * Удаляет конфигурацию модального окна.
   * @param {string} name - Имя модального окна.
   */
  public static remove(name: string): void {
    if (this.configs.has(name)) {
      this.configs.delete(name)
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Modal with name "${name}" does not exist.`)
    }
  }

  /**
   * Получает конфигурацию модального окна.
   * @param {string} name - Имя модального окна.
   * @return {IModalConfig | undefined} Конфигурация модального окна.
   */
  public static get(name: string): IModalConfig | undefined {
    return this.configs.get(name)
  }

  /**
   * Проверяет, существует ли конфигурация для модального окна.
   * @param {string} name - Имя модального окна.
   * @return {boolean} true, если конфигурация существует.
   */
  public static has(name: string): boolean {
    return this.configs.has(name)
  }
}
